// El armario: ver, filtrar y arreglar.
//
// Los filtros no son decoración. Son la herramienta de gestión que
// pidió el usuario: "mostrame las gastadas" y decidís qué donás,
// arreglás o reemplazás. Por eso cada filtro muestra su conteo antes
// de que lo aprietes — enterarte de que tenés 7 prendas gastadas no
// debería costar un clic.
//
// Los conteos vienen del backend calculados sobre el armario COMPLETO,
// así que siguen siendo verdad mientras hay un filtro puesto.

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { prepararFoto } from "./imagen";
import EditarPrenda from "./EditarPrenda";
import Visor from "./Visor";
import { cargarOpciones } from "./opciones";
import { haceCuanto } from "./fechas";
import { fotoChica, fotoGrande } from "./foto";
import { t, tCat, tPlural, useIdioma, tError, idiomaActual, numero } from "./i18n";
import { useFocoAtrapado } from "./foco";

export default function Armario({ recargarToken, onVerCombinaciones }) {
  // Re-renderiza si cambia el idioma: `tCat` se resuelve en el render.
  useIdioma();
  const [opciones, setOpciones] = useState(null);
  const [datos, setDatos] = useState(null);
  const [filtros, setFiltros] = useState({});
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [editando, setEditando] = useState(null);
  const [deshacer, setDeshacer] = useState(null);

  // La foto ampliada vive acá y no en cada tarjeta: así hay un solo
  // visor abierto por vez, sin importar cuántas prendas haya en pantalla.
  const [ampliada, setAmpliada] = useState(null);

  // Dos estados para una sola caja de texto:
  //   `texto`   es lo que se ve mientras escribís (cambia en cada tecla)
  //   `filtros.q` es lo que se le pide al servidor
  //
  // Separarlos es lo que permite escribir sin que cada letra dispare una
  // consulta. Escribir "camisa" serían seis.
  const [texto, setTexto] = useState("");

  // Los filtros arrancan PLEGADOS. Cuatro grupos de chips ocupaban
  // media pantalla de celular antes de que se viera una sola prenda, y
  // la mayoría de las veces se entra al armario a mirar la ropa, no a
  // filtrar. Plegados siguen a un toque de distancia y se ve arriba
  // cuáles están puestos, que es la información que importa cuando
  // están cerrados.
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);

  useEffect(() => {
    cargarOpciones().then(setOpciones);
  }, []);

  /* CUÁNTAS PRENDAS SE PIDEN.
   *
   * 60 alcanza para llenar varias pantallas de scroll en cualquier
   * celular, y para un armario típico (50-200 prendas) es casi todo.
   *
   * Antes se pedían TODAS siempre. Con datos realistas eso son 44 kB
   * comprimidos con 171 prendas y 126 kB con 500 — y el servidor firmaba
   * las 500 URLs de fotos para mostrar las primeras 20 que entran en
   * pantalla.
   *
   * Se pide un límite CRECIENTE en vez de ir agregando páginas al final:
   * "ver más" vuelve a pedir desde el principio con un tope más grande.
   * Es un poco más de tráfico para quien recorre el armario entero —que
   * es raro— y a cambio no hay dos fuentes de verdad ni listas que
   * mezclar cuando se edita una prenda o se cambia un filtro. El bug de
   * "edité algo y la lista quedó rara" no puede existir.
   */
  const POR_PAGINA = 60;
  const [mostrar, setMostrar] = useState(POR_PAGINA);

  // Cambiar un filtro vuelve a la primera página. Sin esto, filtrar
  // después de haber tocado "ver más" traería 300 prendas de un
  // resultado de 12.
  useEffect(() => {
    setMostrar(POR_PAGINA);
  }, [filtros]);

  /* ⚠️  LA RESPUESTA VIEJA NO PUEDE PISAR A LA NUEVA (ME-6, arreglado
   *     el 2026-09-03).
   *
   * El buscador tiene debounce de 300 ms, pero eso agrupa las TECLAS,
   * no las RESPUESTAS. Dos pedidos en vuelo vuelven en el orden que la
   * red quiera, y el último en llegar es el que se dibuja — que no es
   * el último que se pidió.
   *
   * Se ve así: escribís "jean", tarda; escribís "camisa", contesta
   * rápido; llega la de "jean" y la pantalla muestra jeans con "camisa"
   * escrito en la caja. El usuario no puede entenderlo ni reportarlo:
   * los datos y el filtro que se ven no coinciden y nada dice por qué.
   *
   * Un contador y no un AbortController: abortar necesitaría que
   * `api.js` acepte una señal en cada llamada, y lo que hay que
   * garantizar no es ahorrar el request —ya salió— sino que su
   * respuesta no se dibuje. El contador lo hace sin tocar la API. */
  const pedidoVigente = useRef(0);

  const traer = useCallback(async () => {
    const mio = ++pedidoVigente.current;
    try {
      const d = await api.listarPrendas({ ...filtros, limite: mostrar });
      if (mio !== pedidoVigente.current) return; // llegó tarde: ya hay otro
      setError("");
      setDatos(d);
    } catch (e) {
      if (mio !== pedidoVigente.current) return;
      setError(tError(e));
    } finally {
      // También el spinner: apagarlo desde un pedido viejo diría
      // "listo" mientras el nuevo todavía viene en camino.
      if (mio === pedidoVigente.current) setCargando(false);
    }
  }, [filtros, mostrar]);

  useEffect(() => {
    traer();
  }, [traer, recargarToken]);

  /* ⚠️  ACÁ ARRIBA, CON LOS DEMÁS HOOKS. NO ABAJO. (2026-09-06)
   *
   * Esta línea estuvo veinte minutos en producción escrita 180 líneas
   * más abajo, junto a `prendaEditando`, que es donde "quedaba linda".
   * El problema: ahí abajo está DESPUÉS de dos returns tempranos
   *
   *     if (cargando || !opciones) return <p>Cargando…</p>;
   *     if (error && !datos) return <div>…</div>;
   *
   * así que mientras el armario cargaba el hook NO se llamaba, y cuando
   * llegaban los datos sí. React cuenta los hooks de cada render: uno de
   * más es el error #310, y desmonta el árbol entero. La app quedaba en
   * la pantalla de "algo se rompió" apenas se abría el armario.
   *
   * Se le pasa `editando` (el id) y no `prendaEditando` (el objeto) a
   * propósito: el objeto se busca abajo, y necesitarlo acá es lo que
   * tentaba a bajar el hook. Con el id alcanza — si el id existe pero la
   * prenda no se encuentra, el modal no se dibuja, el ref queda en null
   * y el hook no hace nada.
   *
   * `test_hooks.js` corre la regla de ESLint sobre todo `src/` para que
   * esto no vuelva a pasar en ninguna pantalla. */
  const cajaEditar = useFocoAtrapado(!!editando);

  // Con el modal abierto, el fondo no se mueve. Sin esto, en el celular
  // se scrollea la grilla de atrás y al cerrar aparecés en otro lugar
  // del armario.
  useEffect(() => {
    if (!editando) return;
    const previo = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const alTeclear = (e) => {
      if (e.key === "Escape") setEditando(null);
    };
    window.addEventListener("keydown", alTeclear);

    return () => {
      document.body.style.overflow = previo;
      window.removeEventListener("keydown", alTeclear);
    };
  }, [editando]);

  // 300 ms después de la última tecla se consulta. Suficiente para no
  // pegarle al servidor en cada letra, y poco como para que se sienta
  // instantáneo.
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltros((antes) => {
        const limpio = texto.trim();
        if ((antes.q || "") === limpio) return antes; // sin cambio real
        const nuevos = { ...antes };
        if (limpio) nuevos.q = limpio;
        else delete nuevos.q;
        return nuevos;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [texto]);

  // Apretar el filtro que ya está puesto lo saca. Sin esto haría falta
  // un botón "quitar" al lado de cada uno.
  function alternar(clave, valor) {
    setFiltros((antes) => {
      const nuevos = { ...antes };
      if (nuevos[clave] === valor) delete nuevos[clave];
      else nuevos[clave] = valor;
      // color y familia_color se pisan: son dos formas de preguntar lo
      // mismo y juntas se contradicen.
      if (clave === "familia_color") delete nuevos.color;
      if (clave === "color") delete nuevos.familia_color;
      return nuevos;
    });
  }

  async function cambiarCampo(id, campo, valor) {
    // Optimista: la prenda cambia en pantalla al instante y se corrige
    // sola si el servidor la rechaza. Marcar cinco prendas como
    // gastadas no debería tener cinco esperas.
    setDatos((antes) => ({
      ...antes,
      prendas: antes.prendas.map((p) =>
        p.id === id ? { ...p, [campo]: valor } : p
      ),
    }));

    try {
      await api.editarPrenda(id, { [campo]: valor });
      traer(); // refresca los conteos
    } catch (e) {
      setError(tError(e));
      traer();
    }
  }

  // Apuntar la prenda a la foto nueva. El PATCH borra la vieja de
  // Storage del lado del servidor, después de confirmar que la fila
  // quedó actualizada.
  // La miniatura viaja SIEMPRE con la foto, aunque venga null: el
  // servidor borra la miniatura vieja cuando cambia la grande, y sin
  // este campo la prenda quedaría con la foto nueva y la miniatura
  // anterior — la grilla mostrando una prenda y la ficha otra.
  async function cambiarFoto(id, foto_path, foto_mini_path) {
    await api.editarPrenda(id, { foto_path, foto_mini_path: foto_mini_path || null });
    await traer();
  }

  async function guardarEdicion(id, cambios) {
    await api.editarPrenda(id, cambios);
    setEditando(null);
    await traer();
  }

  /* "Prenda fichada" NO es un campo del formulario, acá igual que en la
   * ficha, y por el mismo motivo: el formulario se guarda con un botón
   * y CIERRA el modal. Marcar que algo se mostró es un clic que se da
   * mientras mirás la prenda; cerrarte la pantalla por darlo sería
   * castigarte por usarlo.
   *
   * Hasta el 2026-08-26 este botón existía SOLO en la ficha. Desde el
   * armario se podía editar todo —nombre, colores, estado, ocasiones—
   * menos esto, y no había ninguna señal de que faltara: el modal se
   * veía completo. Un hueco así no se reporta, se sufre en silencio.
   *
   * Optimista: la marca cambia antes de que el servidor conteste, y si
   * falla se revierte y se dice. Después se refresca la grilla, que
   * dibuja la misma marca. */
  async function alternarFichada(prenda, nueva) {
    if (!prenda) return;
    setDatos((antes) =>
      antes
        ? {
            ...antes,
            prendas: antes.prendas.map((p) =>
              p.id === prenda.id ? { ...p, fichada: nueva } : p
            ),
          }
        : antes
    );
    try {
      await api.editarPrenda(prenda.id, { fichada: nueva });
      await traer();
    } catch (e) {
      // Se revierte y se relanza: el error lo muestra el formulario,
      // que es donde está el botón que lo provocó.
      setDatos((antes) =>
        antes
          ? {
              ...antes,
              prendas: antes.prendas.map((p) =>
                p.id === prenda.id ? { ...p, fichada: !nueva } : p
              ),
            }
          : antes
      );
      throw e;
    }
  }

  // Borrar con deshacer, en vez de con un cartel de confirmación.
  //
  // El borrado del servidor es blando (`activa = false`), así que
  // deshacerlo es solo volver a prenderla. Y un "deshacer" molesta
  // menos que un "¿estás seguro?" en cada prenda: el que borra bien
  // no paga nada, y el que se equivoca tiene salida.
  async function borrar(prenda) {
    // Sale de la lista al instante. Si el servidor falla, vuelve.
    setDatos((antes) => ({
      ...antes,
      prendas: antes.prendas.filter((p) => p.id !== prenda.id),
    }));

    try {
      await api.borrarPrenda(prenda.id);
      setDeshacer({ id: prenda.id, nombre: prenda.nombre });
      // El aviso dura lo suficiente para leerlo y reaccionar, no tanto
      // como para quedar estorbando.
      setTimeout(
        () => setDeshacer((d) => (d?.id === prenda.id ? null : d)),
        8000
      );
      await traer();
    } catch (e) {
      setError(tError(e));
      await traer();
    }
  }

  async function deshacerBorrado() {
    if (!deshacer) return;
    const id = deshacer.id;
    setDeshacer(null);
    try {
      await api.restaurarPrenda(id);
      await traer();
    } catch (e) {
      setError(t("armario.noSeRecupero", "No se pudo recuperar la prenda: {motivo}", { motivo: tError(e) }));
    }
  }

  if (cargando || !opciones) return <p className="tenue">{t("armario.cargando", "Cargando tu armario...")}</p>;
  if (error && !datos) return <div className="mensaje-error" role="alert">{error}</div>;

  // La prenda abierta en el modal. Se busca por id y no se guarda el
  // objeto: si se guardara, después de editar mostraría los datos
  // viejos hasta cerrar y volver a abrir.
  const prendaEditando = editando
    ? datos?.prendas.find((x) => x.id === editando) || null
    : null;

  const r = datos?.resumen || {};
  // El orden no filtra nada: si fuera parte de `hayFiltros`, cambiar el
  // orden diría "34 de 34 prendas" y ofrecería limpiar un filtro que no
  // existe.
  const hayFiltros = Object.keys(filtros).some((k) => k !== "orden");

  // Los que se dibujan como chips: ni el orden (no filtra) ni la
  // búsqueda (ya se ve escrita en su propia caja, repetirla como chip
  // sería decir lo mismo dos veces).
  const chips = Object.entries(filtros).filter(
    ([k]) => k !== "orden" && k !== "q"
  );

  return (
    <section>
      <div className="buscador">
        <input
          type="search"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder={t("armario.buscar", "Buscar: jean, camisa, denim, marron...")}
          aria-label={t("armario.buscarEnArmario", "Buscar en el armario")}
        />
        {texto && (
          <button
            type="button"
            className="buscador-limpiar"
            onClick={() => setTexto("")}
            aria-label={t("armario.limpiarBusqueda", "Limpiar búsqueda")}
          >
            ×
          </button>
        )}
      </div>

      <div className="barra-compacta">
        <button
          className={`boton-secundario ${chips.length ? "boton-con-punto" : ""}`}
          onClick={() => setFiltrosAbiertos((a) => !a)}
        >
          {t("armario.filtros", "Filtros")} {chips.length > 0 ? `(${numero(chips.length)})` : ""}
          {filtrosAbiertos ? " ▲" : " ▼"}
        </button>

        <label className="selector">
          <span className="selector-etiqueta">{t("armario.orden", "Orden")}</span>
          <select
            value={filtros.orden || "recientes"}
            onChange={(e) => {
              const v = e.target.value;
              setFiltros((antes) => {
                const nuevos = { ...antes };
                if (v === "recientes") delete nuevos.orden;
                else nuevos.orden = v;
                return nuevos;
              });
            }}
          >
            {/* ⚠️  ETIQUETA QUE MANDA EL SERVIDOR, EN ESPAÑOL.
                Es una clase que ningún guardián de idioma veía: los
                tres miran `src/`, y acá no hay una palabra en español
                — el texto viaja en el JSON de /api/opciones. Se traduce
                por `valor`, con lo que manda el servidor de respaldo.
                Ver lib/opciones_api.js y test_opciones_api.js. */}
            {(opciones.ordenes || []).map((o) => (
              <option key={o.valor} value={o.valor}>
                {t(`opciones.orden.${o.valor}`, o.etiqueta)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Lo puesto se ve SIEMPRE, aunque el panel esté cerrado. Un
          filtro activo e invisible es la forma más rápida de que
          alguien crea que perdió prendas. */}
      {chips.length > 0 && !filtrosAbiertos && (
        <div className="opciones-chips chips-activos">
          {chips.map(([k, v]) => (
            <button key={k} className="chip chip-activo" onClick={() => alternar(k, v)}>
              {textoDeChip(k, v)} ×
            </button>
          ))}
        </div>
      )}

      <div className={`filtros ${filtrosAbiertos ? "" : "filtros-ocultos"}`}>
        {/* "Ya se mostró en público".

            ⚠️  LA GUARDA `r.fichada?.si > 0` NO ES OPCIONAL, Y EL
            COMENTARIO QUE ESTABA ACÁ MENTÍA. Decía que "los conteos
            hacen que desaparezca solo mientras no haya ninguna
            fichada". No: `GrupoFiltro` esconde el grupo solo si TODOS
            los valores dan cero, y "Sin mostrar" cuenta el armario
            ENTERO. Con cero fichadas el conteo es {si: 0, no: 200} —
            el grupo se dibujaba igual, arriba de todo, con un único
            chip "Sin mostrar (200)" que no filtra nada.

            O sea: el filtro aparecía primero para TODOS los usuarios,
            incluidos los que nunca usaron la función, ofreciendo un
            camino que devuelve el armario completo. Medido con la
            lógica real de GrupoFiltro, no supuesto.

            Va primero porque quien fichó ropa lo va a buscar; y ahora
            solo lo ve quien fichó ropa. */}
        {r.fichada?.si > 0 && (
          <GrupoFiltro
            titulo={t("armario.fichada", "Mostrada en público")}
            valores={["si", "no"]}
            etiquetas={{
              si: t("armario.fichadaSi", "Ya la mostré"),
              no: t("armario.fichadaNo", "Sin mostrar"),
            }}
            conteos={r.fichada}
            activo={filtros.fichada}
            onElegir={(v2) => alternar("fichada", v2)}
          />
        )}

        <GrupoFiltro
          titulo={t("armario.estado", "Estado")}
          grupo="estado"
          valores={opciones.estados}
          conteos={r.estado}
          activo={filtros.estado}
          onElegir={(v) => alternar("estado", v)}
        />

        <GrupoFiltro
          titulo={t("armario.disponibilidad", "Disponibilidad")}
          grupo="disponibilidad"
          valores={opciones.disponibilidades}
          conteos={r.disponibilidad}
          activo={filtros.disponibilidad}
          onElegir={(v) => alternar("disponibilidad", v)}
        />

        <GrupoFiltro
          titulo={t("armario.color", "Color")}
          grupo="familiaColor"
          valores={Object.keys(r.familiaColor || {})}
          conteos={r.familiaColor}
          activo={filtros.familia_color}
          onElegir={(v) => alternar("familia_color", v)}
        />

        {/* Las ocasiones salen del RESUMEN, no de `opciones.categorias`.
            Es la misma decisión que en Color: se ofrecen las que
            realmente tenés, con el conteo al lado. Una ocasión con cero
            prendas en un filtro es un callejón sin salida — la tocás y
            te quedás mirando una pantalla vacía.

            Vienen ordenadas por cantidad desde el servidor. */}
        <GrupoFiltro
          titulo={t("armario.ocasion", "Ocasión")}
          grupo="ocasion"
          valores={Object.keys(r.categoria || {})}
          conteos={r.categoria}
          activo={filtros.categoria}
          onElegir={(v) => alternar("categoria", v)}
        />

        {/* Estación. Va después de Ocasión y antes de Tipo: la
            pregunta "¿qué tengo para el verano?" es de gestión —qué
            guardo, qué saco del placard— y esa es la razón por la que
            existe esta pantalla. Tipo es más de búsqueda, y para buscar
            está el buscador.

            Los valores salen del RESUMEN y no de `opciones.estaciones`:
            una prenda de Primavera+Verano suma en las dos, y un armario
            sin nada de invierno no ofrece un chip que devuelve vacío.
            Mismo criterio que Color y Ocasión. */}
        <GrupoFiltro
          titulo={t("armario.estacion", "Estación")}
          grupo="estacion"
          valores={Object.keys(r.estacion || {})}
          conteos={r.estacion}
          activo={filtros.estacion}
          onElegir={(v) => alternar("estacion", v)}
        />

        <GrupoFiltro
          titulo={t("armario.tipo", "Tipo")}
          grupo="tipo"
          valores={opciones.tipos}
          conteos={null}
          activo={filtros.tipo}
          onElegir={(v) => alternar("tipo", v)}
        />

        {/* Subcategoría y no prenda concreta: 103 chips no son un
            filtro, son un catálogo. La prenda exacta se encuentra con
            el buscador de texto, que ya busca por nombre.

            Igual que Color y Ocasión, salen del resumen: solo las que
            tenés. */}
        <GrupoFiltro
          titulo={t("armario.subcategoria", "Subcategoría")}
          grupo="subcategoria"
          valores={Object.keys(r.subcategoria || {})}
          conteos={r.subcategoria}
          activo={filtros.subcategoria}
          onElegir={(v) => alternar("subcategoria", v)}
        />

        {/* Marca. Sin `grupo` y sin `etiquetas` a propósito: los demás
            filtros muestran valores de catálogo que se guardan en
            español y se traducen al dibujarlos, pero "Levi's" se
            escribe igual en las doce lenguas. Traducir una marca sería
            un error, no una mejora.

            Va último de los chips porque es el único que puede tener
            treinta valores: el armario del dueño tiene 37 marcas
            distintas en 99 prendas, así que la cola son marcas de una
            sola prenda. Por eso salen ordenadas por cantidad y no
            alfabéticas — las que sirven para filtrar están arriba.

            Si nadie cargó ninguna marca todavía, `r.marca` es `{}` y
            este grupo no se dibuja. */}
        <GrupoFiltro
          titulo={t("armario.marca", "Marca")}
          valores={Object.keys(r.marca || {})}
          conteos={r.marca}
          activo={filtros.marca}
          onElegir={(v) => alternar("marca", v)}
        />

        <div className="grupo-filtro">
          <p className="filtro-titulo">{t("armario.otros", "Otros")}</p>
          <div className="opciones-chips">
            <button
              type="button"
              className={`chip ${filtros.sin_foto === "1" ? "chip-activo" : ""}`}
              onClick={() => alternar("sin_foto", "1")}
            >
              {t("armario.sinFoto", "Sin foto")} {r.sinFoto != null && `(${numero(r.sinFoto)})`}
            </button>
            {/* «Sin usar todavía»: la promesa de la base de marca («con
                todo lo que tienes»). Auditoría 2026-09-15, §2.4: era
                solo la etiqueta de cada tarjeta, no un filtro. Se dibuja
                mientras haya alguna prenda sin uso, como el resto. */}
            {r.sinUso > 0 && (
              <button
                type="button"
                className={`chip ${filtros.sin_uso === "1" ? "chip-activo" : ""}`}
                onClick={() => alternar("sin_uso", "1")}
              >
                {t("armario.sinUsar", "Sin usar todavía")} ({numero(r.sinUso)})
              </button>
            )}
            {/* Cuántas prendas todavía no dicen qué son. Se muestra
                mientras no sea 0, porque hasta entonces el filtro de
                subcategoría miente por omisión: no lista las que no
                tienen el dato, y desde afuera eso se ve igual que "no
                tengo ninguna". */}
            {r.sinPrenda > 0 && (
              <span className="chip chip-inerte tenue">
                {t("armario.sinClasificar", "{n} sin clasificar", { n: r.sinPrenda })}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="barra-lote">
        <div>
          {hayFiltros ? (
            <>
              <strong>{numero(datos.total)}</strong> {t("armario.deTotalPrendas", "de {total} prendas", { total: datos.totalArmario })}
            </>
          ) : (
            <>
              <strong>{numero(datos.totalArmario)}</strong> {t("armario.prendas", "prendas")}
            </>
          )}
        </div>

        {hayFiltros && (
          <button
            className="boton-texto"
            onClick={() => {
              setTexto("");
              // Se limpian los filtros, pero el orden elegido se
              // respeta: no es algo que el usuario quiera deshacer.
              setFiltros((antes) =>
                antes.orden ? { orden: antes.orden } : {}
              );
            }}
          >
            {t("armario.limpiarFiltros", "Limpiar filtros")}
          </button>
        )}
      </div>

      {error && <div className="mensaje-error" role="alert">{error}</div>}

      {deshacer && (
        <div className="barra-deshacer">
          <span>
            {t("armario.borraste", "Borraste")} <strong>{deshacer.nombre}</strong>
          </span>
          <button className="boton-texto" onClick={deshacerBorrado}>
            {t("armario.deshacer", "Deshacer")}
          </button>
        </div>
      )}

      {datos.prendas.length === 0 ? (
        <p className="tenue">
          {filtros.q
            ? t("armario.nadaCoincideCon", 'Ninguna prenda coincide con "{q}".', { q: filtros.q })
            : hayFiltros
            ? t("armario.nadaConFiltros", "Ninguna prenda con esos filtros.")
            : t("armario.vacio", "Tu armario está vacío. Carga fotos para empezar.")}
        </p>
      ) : (
        <div className="grilla-prendas">
          {datos.prendas.map((p) => (
            <PrendaTarjeta
              key={p.id}
              prenda={p}
              opciones={opciones}
              onCambiar={cambiarCampo}
              onFotoCambiada={cambiarFoto}
              onAbrirEdicion={() => setEditando(p.id)}
              onBorrar={() => borrar(p)}
              onAmpliar={() =>
                fotoGrande(p) && setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
              }
              onVerCombinaciones={onVerCombinaciones}
            />
          ))}
        </div>
      )}

      {/* "Ver más" y no scroll infinito.
       *
       * El scroll infinito hace imposible llegar al pie de la pantalla y
       * secuestra el botón de "atrás" del navegador. Un botón dice
       * cuántas quedan y deja al usuario decidir.
       *
       * `hayMas` lo dice el servidor. Sin ese dato la pantalla tendría
       * que adivinar comparando cuántas pidió contra cuántas llegaron —
       * que falla justo cuando el total es múltiplo del tamaño de página
       * y muestra un botón que no trae nada. */}
      {datos.hayMas && (
        <div className="centrado">
          <button
            type="button"
            className="boton-secundario"
            onClick={() => setMostrar((m) => m + POR_PAGINA)}
            disabled={cargando}
          >
            {t("armario.verMas", "Ver más")}
            <span className="tenue texto-mini">
              {" "}
              — {t("armario.mostrando", "mostrando {n} de {total}", { n: datos.prendas.length, total: datos.totalFiltradas })}
            </span>
          </button>
        </div>
      )}

      {/* La edición va en un MODAL y no dentro de la tarjeta.

          Antes se renderizaba adentro de la tarjeta, que en el celular
          es una columna de ~150 px: el formulario entero quedaba
          apretado ahí y había que bajar hasta encontrarlo. Un panel de
          edición tiene que tener prioridad de pantalla mientras está
          abierto — es lo único que estás haciendo en ese momento. */}
      {prendaEditando && (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-label={t("armario.editarPrenda", "Editar {nombre}", { nombre: prendaEditando.nombre })}
          onClick={() => setEditando(null)}
        >
          <div className="modal-caja" ref={cajaEditar} onClick={(e) => e.stopPropagation()}>
            <div className="modal-cabecera">
              {fotoChica(prendaEditando) && (
                <img loading="lazy" decoding="async"
                  src={fotoChica(prendaEditando)}
                  alt=""
                  className="modal-foto"
                />
              )}
              <h2 className="modal-titulo">{prendaEditando.nombre}</h2>
              <button
                className="modal-cerrar"
                onClick={() => setEditando(null)}
                aria-label={t("comun.cerrar", "Cerrar")}
              >
                ×
              </button>
            </div>

            <div className="modal-cuerpo">
              <EditarPrenda
                prenda={prendaEditando}
                opciones={opciones}
                onGuardar={(c) => guardarEdicion(prendaEditando.id, c)}
                onCancelar={() => setEditando(null)}
                onFichada={(nueva) => alternarFichada(prendaEditando, nueva)}
              />
            </div>
          </div>
        </div>
      )}

      <Visor
        url={ampliada?.url}
        alt={ampliada?.alt}
        prenda={ampliada?.prenda}
        onPrendaEditada={traer}
        onCerrar={() => setAmpliada(null)}
      />
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
   EL TEXTO DE UN CHIP DE FILTRO ACTIVO
   ══════════════════════════════════════════════════════════════════

   Los chips de arriba muestran lo que está filtrado cuando el panel
   está cerrado. Dibujaban el VALOR crudo —"Buena", "Casual",
   "Superior", "Verano"— o sea el español guardado, en las once
   interfaces que no lo son.

   Es el mismo bug que `GrupoFiltro` y por el mismo motivo, con una
   vuelta más: acá el grupo no viene por prop, hay que deducirlo de la
   CLAVE del filtro. Por eso el mapa.

   ⚠️  UNA CLAVE QUE FALTE EN ESTE MAPA NO DA ERROR: cae al valor
       crudo, que es exactamente el bug. El guardián es `test_i18n.js`
       §12: compara este mapa contra las claves que la pantalla le pasa
       a `alternar()`.

   ⚠️  ESE GUARDIÁN NO EXISTÍA HASTA EL 2026-08-29, Y ESTE COMENTARIO
       DECÍA QUE SÍ. Se buscó en los 47 archivos de prueba y no estaba
       en ninguno. Un comentario que promete un test inexistente es peor
       que no tenerlo: el que agrega un filtro lo lee, confía y no
       chequea. Si volvés a leer una promesa así en este repo, buscala
       antes de creerle.

   Las claves que NO son de catálogo —`sin_foto`, `fichada`, `marca`—
   no van acá y están justificadas una por una en ese test. */
const GRUPO_DE_FILTRO = {
  estado: "estado",
  disponibilidad: "disponibilidad",
  familia_color: "familiaColor",
  color: "color",
  categoria: "ocasion",
  tipo: "tipo",
  subcategoria: "subcategoria",
  estacion: "estacion",
};

function textoDeChip(k, v) {
  // Los dos que no son valores de catálogo: banderas, no palabras.
  if (k === "sin_foto") return t("armario.sinFoto", "Sin foto");
  if (k === "sin_uso") return t("armario.sinUsar", "Sin usar todavía");
  if (k === "fichada") {
    return v === "si"
      ? t("armario.fichadaSi", "Ya la mostré")
      : t("armario.fichadaNo", "Sin mostrar");
  }
  const grupo = GRUPO_DE_FILTRO[k];
  return grupo ? tCat(grupo, v) : v;
}

function GrupoFiltro({ titulo, valores, conteos, activo, onElegir, etiquetas, grupo }) {
  // Un filtro que no tiene nada que ofrecer no se dibuja.
  const utiles = conteos
    ? valores.filter((v) => (conteos[v] || 0) > 0)
    : valores;

  if (!utiles.length) return null;

  return (
    <div className="grupo-filtro">
      <p className="filtro-titulo">{titulo}</p>
      <div className="opciones-chips">
        {utiles.map((v) => (
          <button
            key={v}
            type="button"
            className={`chip ${activo === v ? "chip-activo" : ""}`}
            onClick={() => onElegir(v)}
          >
            {/* ⚠️  EL VALOR QUE VIAJA AL SERVIDOR NO ES EL TEXTO QUE SE
                MUESTRA, Y ACÁ SE OLVIDÓ DURANTE MESES.

                Los valores del catálogo se guardan en español para
                siempre y se traducen al dibujarlos. Esta línea decía
                `{v}` a secas: TODOS los chips de filtro del armario
                —Estado, Disponibilidad, Ocasión, Tipo, Color— se veían
                en castellano en las once interfaces que no lo son.

                No lo agarró el detector de valores crudos (tanda 8)
                porque `valores` es una PROP: desde acá no hay forma de
                saber que adentro viene un valor de catálogo. Es
                exactamente el punto ciego que ese detector declara en
                su cabecera, encontrado a mano tres horas después.

                `etiquetas` sigue existiendo para los grupos que no son
                catálogo: "si"/"no" son claves, no palabras. */}
            {etiquetas ? etiquetas[v] : grupo ? tCat(grupo, v) : v}
            {conteos && <span className="chip-conteo"> {numero(conteos[v])}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function PrendaTarjeta({
  prenda,
  opciones,
  onCambiar,
  onFotoCambiada,
  onAbrirEdicion,
  onBorrar,
  onAmpliar,
  onVerCombinaciones,
}) {
  const p = prenda;
  const noDisponible = p.disponibilidad !== "Disponible";

  const inputRef = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorFoto, setErrorFoto] = useState("");

  // Cambiar la foto: la prenda ya está clasificada, así que la nueva
  // imagen no pasa por la IA. Se sube, se apunta la prenda a ella, y el
  // servidor borra la vieja — que si no quedaría ocupando lugar sin que
  // nadie la use.
  async function cambiarFoto(archivo) {
    if (!archivo) return;

    setSubiendo(true);
    setErrorFoto("");

    try {
      const foto = await prepararFoto(archivo);
      const { foto_path, foto_mini_path } = await api.subirFoto(foto);
      await onFotoCambiada(p.id, foto_path, foto_mini_path);
    } catch (e) {
      setErrorFoto(tError(e));
    } finally {
      setSubiendo(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <article className={`prenda ${noDisponible ? "prenda-apagada" : ""}`}>
      <div className="prenda-foto-caja">
        {fotoChica(p) ? (
          <img loading="lazy" decoding="async"
            src={fotoChica(p)}
            alt={p.nombre}
            className="prenda-foto"
            onClick={onAmpliar}
            title={t("armario.ampliar", "Ampliar")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              /* Enter y espacio hacen lo mismo que el click, sea lo que
                 sea. No se duplica la expresión del onClick: se dispara
                 el click de verdad. Copiar el handler exigía parsear JSX
                 con llaves anidadas, que es justamente lo que este
                 proyecto ya aprendió a no hacer. */
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.currentTarget.click();
              }
            }}
          />
        ) : (
          <div className="prenda-foto prenda-sin-foto">
            <span className="tenue texto-mini">{t("armario.sinFoto", "Sin foto")}</span>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => cambiarFoto(e.target.files?.[0])}
          style={{ display: "none" }}
        />

        <button
          type="button"
          className="boton-sobre-foto"
          onClick={() => inputRef.current?.click()}
          disabled={subiendo}
        >
          {subiendo ? t("armario.subiendo", "Subiendo...") : fotoChica(p) ? t("armario.cambiarFoto", "Cambiar foto") : t("armario.ponerFoto", "Poner foto")}
        </button>
      </div>

      <div className="prenda-cuerpo">
        <p className="prenda-nombre">{p.nombre}</p>

        {/* La marca, si se sabe. Sin etiqueta "Marca:" delante: en una
            tarjeta chica la palabra ocupa más que el dato y no aclara
            nada —nadie lee "Levi's" debajo del nombre de un jean y se
            pregunta qué es—. La etiqueta sí está en el formulario de
            edición, donde hace falta para saber qué escribir.

            No se dibuja nada cuando falta, y esa es la diferencia entre
            ausencia y cero: una prenda sin marca leída no dice "sin
            marca", se calla. Puede ser que la IA no la haya visto, o
            que la prenda no tenga logo, y la tarjeta no sabe cuál de
            las dos. */}
        {p.marca && <p className="texto-mini prenda-marca">{p.marca}</p>}

        {errorFoto && <p className="mensaje-error texto-mini" role="alert">{errorFoto}</p>}

        {/* Cuándo la usaste por última vez. Es la información que
            convierte el armario de inventario en herramienta: una prenda
            que no tocás hace ocho meses es una decisión pendiente
            (¿la donás? ¿la arreglás? ¿te olvidaste de que la tenías?). */}
        <p className="texto-mini prenda-uso">
          <span className={p.ultimo_uso ? "" : "tenue"}>
            {haceCuanto(p.ultimo_uso, {
              nunca: t("armario.sinUsar", "Sin usar todavía"),
              idioma: idiomaActual(),
            })}
          </span>
          {p.veces_usada > 0 && (
            <span className="tenue">
              {" "}
              {/* ⚠️  `tPlural` Y NO UN TERNARIO, y no es prolijidad.
                  Esto decía `veces_usada === 1 ? "vez" : "veces"`. Dos
                  formas alcanzan en español y en inglés; el ruso tiene
                  tres y el árabe seis. Con dos, el ruso dice "5 раза"
                  —que es como decir "5 vez"— y el árabe pierde el dual.
                  La traducción rusa ya se había rendido sola: decía
                  `veces: "раз(а)"`, con el paréntesis adentro. */}
              · {tPlural("plural.veces", p.veces_usada)}
            </span>
          )}
        </p>

        {/* Cuántos outfits habilita. Es el número que convierte "tengo
            una camisa" en "esta camisa me sirve para 60 conjuntos y esta
            otra para 3". El cero es el dato más valioso de todos: una
            prenda que no entra en ninguna combinación es plata parada. */}
        {/* ⚠️  ABRIGO Y ACCESORIOS NO COMPITEN POR ENTRAR: se suman
            arriba de cualquier conjunto. Su conteo viene en `null` a
            propósito (ver contarPorPrenda) — con 0, esta tarjeta les
            decía "No entra en ninguna combinación" a los 26 abrigos
            del armario, textual y falso. Se decide por `cubre` y no
            por el null: el null también significa "el conteo se
            truncó", y esos dos silencios no son el mismo. */}
        {(p.cubre || []).length === 0 && (
          <p className="texto-mini prenda-combis tenue">
            {t("armario.complemento", "Se suma encima de cualquier conjunto")}
          </p>
        )}
        {(p.cubre || []).length > 0 && p.combinaciones !== null && p.combinaciones !== undefined && (
          <p className="texto-mini prenda-combis">
            {p.combinaciones === 0 ? (
              <span className="prenda-sin-combis">
                {t("armario.sinCombis", "No entra en ninguna combinación")}
              </span>
            ) : (
              <button
                type="button"
                className="enlace-combis"
                onClick={() => onVerCombinaciones(p)}
              >
                {tPlural("plural.combinaciones", p.combinaciones)} →
              </button>
            )}
          </p>
        )}

        <div className="etiquetas">
          <span className="etiqueta">{tCat("tipo", p.tipo)}</span>
          {p.talla && <span className="etiqueta etiqueta-talla">{p.talla}</span>}
          {(p.colores || []).map((c) => (
            <span key={c} className="etiqueta etiqueta-color">{tCat("color", c)}</span>
          ))}
        </div>

        {/* Los dos campos que el usuario cambia seguido van editables
            directo en la tarjeta. Entrar a una pantalla de detalle para
            decir "está en el lavadero" es demasiado trabajo para algo
            que se hace todas las semanas. El resto vive en el panel de
            edición. */}
        <div className="ficha-fila">
          <select
            aria-label={t("armario.estadoConservacion", "Estado de conservación")} value={p.estado}
            onChange={(e) => onCambiar(p.id, "estado", e.target.value)}
          >
            {opciones.estados.map((e) => (
              <option key={e} value={e}>{tCat("estado", e)}</option>
            ))}
          </select>

          <select
            aria-label={t("armario.disponibilidad", "Disponibilidad")} value={p.disponibilidad}
            onChange={(e) => onCambiar(p.id, "disponibilidad", e.target.value)}
          >
            {opciones.disponibilidades.map((d) => (
              <option key={d} value={d}>{tCat("disponibilidad", d)}</option>
            ))}
          </select>
        </div>

        <div className="prenda-acciones">
          <button className="boton-secundario texto-mini" onClick={onAbrirEdicion}>
            {t("armario.editar", "Editar")}
          </button>
          {/* Sin cartel de confirmación: el borrado es blando y hay
              deshacer. Preguntar "¿estás seguro?" en cada prenda le
              cobra un clic al que borra bien para proteger al que se
              equivoca. */}
          <button className="boton-texto texto-mini boton-peligro" onClick={onBorrar}>
            {t("armario.borrar", "Borrar")}
          </button>
        </div>
      </div>
    </article>
  );
}
